import { useEffect } from 'react';

type ElRef = { readonly current: HTMLElement | null };

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.getClientRects().length > 0,
  );
}

/**
 * Cycle Tab inside `containerRef` while `active`. Moves focus in on
 * activate and restores the previously focused node on teardown.
 * Tab is left alone when focus is already outside the container
 * (portaled menus).
 */
export function useFocusTrap(containerRef: ElRef, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const root = containerRef.current;
    if (!root) return;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    let cancelled = false;

    queueMicrotask(() => {
      if (cancelled) return;
      const initial = focusables(root)[0];
      if (initial && !root.contains(document.activeElement)) initial.focus();
    });

    function onKey(e: KeyboardEvent): void {
      if (e.key !== 'Tab') return;
      const trap = containerRef.current;
      if (!trap) return;
      const nodes = focusables(trap);
      if (nodes.length === 0) {
        e.preventDefault();
        return;
      }
      const activeEl = document.activeElement;
      if (activeEl instanceof Node && !trap.contains(activeEl)) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey) {
        if (activeEl === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKey, true);
    return () => {
      cancelled = true;
      document.removeEventListener('keydown', onKey, true);
      const target = previouslyFocused;
      queueMicrotask(() => {
        if (target && document.contains(target)) target.focus();
      });
    };
  }, [active, containerRef]);
}

/** React 18 has no `inert` JSX attr — set the DOM property. */
export function useInert(ref: ElRef, inert: boolean): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.inert = inert;
    return () => {
      el.inert = false;
    };
  }, [ref, inert]);
}
